import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { AnvilActiveVariant, AnvilTheme } from "@repo/blacksmith-ui/anvil/animations";
import { ANVIL_TRANSITION_MANIFEST } from "@repo/blacksmith-ui/anvil/manifest";
import { useStableCallback } from "@/hooks/useStableCallback";
import { assetUrl } from "@/lib/assetUrl";

/** A phase still loading this long after it is due hard-swaps to the target loop. */
const LOAD_TIMEOUT_MS = 1_000;
// Seam timings match the Transition Playground story in AnvilWorkingStatus.stories.tsx:
// a swap paints up to one 60Hz frame after setState, and each phase's last
// frame repeats as the next phase's first, so seams are issued one
// generator tick (FRAME_MS in scripts/generate-anvil-variants.mjs) early.
const SWAP_PAINT_LEAD_MS = 17;
const FRAME_MS = 30;
interface Exit {
  startMs: number;
  endMs: number;
  outro: number;
  outroMs: number;
}
interface Shown {
  variant: AnvilActiveVariant;
  theme: AnvilTheme;
  src: string;
  phase: "loop" | "outro" | "intro";
}
const blobs = new Map<string, Promise<Blob>>();
function fetchBlob(path: string): Promise<Blob> {
  let blob = blobs.get(path);
  if (!blob) {
    blob = fetch(path).then(response => {
      if (!response.ok) throw new Error(`${response.status} ${path}`);
      return response.blob();
    });
    blob.catch(() => blobs.delete(path));
    blobs.set(path, blob);
  }
  return blob;
}
function animationPath(name: string, theme: AnvilTheme): string {
  return assetUrl(`anvil-animations/anvil-${name}-${theme}.png`);
}

/**
 * A fresh object URL for `blob`, so a play-once APNG starts at frame 0,
 * decoded before it is shown: swapping an img to a URL that isn't ready yet
 * can paint the outgoing image's first frame in between.
 */
async function decodedSrc(blob: Blob): Promise<string> {
  const src = URL.createObjectURL(blob);
  const img = new Image();
  img.src = src;
  try {
    await img.decode();
  } catch (error) {
    URL.revokeObjectURL(src);
    throw error;
  }
  return src;
}
interface Decoding {
  src: string;
  failed: boolean;
}

/** Like `decodedSrc`, but returns at once so a swap never waits on the decode. */
function startDecode(blob: Blob): Decoding {
  const decoding = {
    src: URL.createObjectURL(blob),
    failed: false
  };
  const img = new Image();
  img.src = decoding.src;
  img.decode().catch(() => {
    decoding.failed = true;
  });
  return decoding;
}
function revokeAll(srcs: string[]): void {
  for (const src of srcs) URL.revokeObjectURL(src);
}

/** Rejects if any blob fails to decode, revoking the ones that did. */
async function decodedSrcs(blobs: Blob[]): Promise<string[]> {
  const results = await Promise.allSettled(blobs.map(decodedSrc));
  const srcs = results.flatMap(result => result.status === "fulfilled" ? [result.value] : []);
  if (srcs.length < results.length) {
    revokeAll(srcs);
    throw new Error("anvil animation failed to decode");
  }
  return srcs;
}
function revokeLater(src: string): void {
  if (!src.startsWith("blob:")) return;
  // After the img has swapped off it.
  setTimeout(() => URL.revokeObjectURL(src), 1_000);
}

/** Every outro of `variant` by `Exit.outro`, named as the generator writes them. */
function outroNames(variant: AnvilActiveVariant): string[] {
  const exits: readonly Exit[] = ANVIL_TRANSITION_MANIFEST[variant].exits;
  const count = Math.max(...exits.map(exit => exit.outro)) + 1;
  return Array.from({
    length: count
  }, (_, outro) => outro === 0 ? `${variant}-outro` : `${variant}-outro-${outro}`);
}

/**
 * The next exit the loop reaches with time left to paint the swap, e.g.
 * running (exits at 0, 480, 960 of 1440) 100ms in → the 480 one, 380ms away.
 */
function nextExit(exits: readonly Exit[], loopMs: number, intoLoop: number): {
  exit: Exit;
  inMs: number;
} {
  for (const exit of exits) {
    if (intoLoop + SWAP_PAINT_LEAD_MS <= exit.endMs) {
      return {
        exit,
        inMs: Math.max(0, exit.startMs - intoLoop)
      };
    }
  }
  return {
    exit: exits[0],
    inMs: loopMs - intoLoop
  };
}
const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, Math.max(0, ms)));
function withLoadTimeout<T>(promise: Promise<T>): Promise<T> {
  return Promise.race([promise, wait(LOAD_TIMEOUT_MS).then(() => {
    throw new Error("anvil transition load timed out");
  })]);
}
const nextPaint = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

/**
 * The src for an `active` anvil that plays `loop → outro → intro → loop`
 * when `target` changes, e.g. sleuthing → sleuthing-outro → running-intro →
 * running. `src` is null while the anvil is not `active`; entering `active`,
 * mounting, or a theme switch shows the target directly. `holding` is true
 * from a target change until its outro starts, so a label that changed with
 * the target can roll in step with the character.
 */
export function useAnvilActiveTransition(target: AnvilActiveVariant | null, theme: AnvilTheme): {
  src: string | null;
  holding: boolean;
} {
  const [shown, setShown] = useState<Shown | null>(null);
  const shownRef = useRef<Shown | null>(null);
  const latestRef = useRef({
    target,
    theme
  });
  const loopStartRef = useRef(0);
  const runningRef = useRef(false);
  /** Bumped by every hard swap, which orphans a transition in flight. */
  const generationRef = useRef(0);
  const mountedRef = useRef(true);
  useLayoutEffect(() => {
    latestRef.current = {
      target,
      theme
    };
  });
  const resting = shown?.theme === theme ? shown : null;
  const current = target === null ? null : resting ?? {
    variant: target,
    theme,
    src: animationPath(target, theme),
    phase: "loop" as const
  };
  const show = useStableCallback((next: Shown | null) => {
    const previous = shownRef.current;
    shownRef.current = next;
    setShown(next);
    if (previous && previous.src !== next?.src) revokeLater(previous.src);
  });
  useEffect(() => {
    const hardSwap = (next_0: Shown | null) => {
      generationRef.current++;
      runningRef.current = false;
      show(next_0);
    };
    if (target === null) {
      if (shownRef.current) hardSwap(null);
      return;
    }
    if (shownRef.current?.theme !== theme) {
      hardSwap({
        variant: target,
        theme,
        src: animationPath(target, theme),
        phase: "loop"
      });
      loopStartRef.current = performance.now();
      return;
    }
    if (shownRef.current.variant === target || runningRef.current) return;
    const generation = generationRef.current;
    const stale = () => !mountedRef.current || generationRef.current !== generation || latestRef.current.target === null || latestRef.current.theme !== theme;
    const transition = async (from: AnvilActiveVariant, to: AnvilActiveVariant) => {
      const fetchAll = (names: string[]) => Promise.all(names.map(name => fetchBlob(animationPath(name, theme))));
      // The next intro and loop keep loading through the exit wait and the
      // outro, so only the (usually prefetched) outros hold the label.
      const nextBlobs = fetchAll([`${to}-intro`, to]);
      nextBlobs.catch(() => {});
      let next_1: Promise<string[]> | undefined;
      let nextTaken = false;
      try {
        const outros = await withLoadTimeout(fetchAll(outroNames(from)));
        if (stale() || latestRef.current.target === from) return false;
        const {
          loopMs,
          exits
        } = ANVIL_TRANSITION_MANIFEST[from];
        const intoLoop = (performance.now() - loopStartRef.current) % loopMs;
        const {
          exit,
          inMs
        } = nextExit(exits, loopMs, intoLoop);
        // Decodes during the exit wait; an exit already due swaps undecoded
        // rather than slipping to the next one.
        const outro = startDecode(outros[exit.outro]);
        await wait(inMs - SWAP_PAINT_LEAD_MS);
        if (stale()) {
          URL.revokeObjectURL(outro.src);
          return false;
        }
        if (outro.failed) {
          URL.revokeObjectURL(outro.src);
          throw new Error("anvil outro failed to decode");
        }
        show({
          variant: from,
          theme,
          src: outro.src,
          phase: "outro"
        });
        await nextPaint();
        // Decoding the next phases any earlier holds up the outro swap, the
        // more so the larger the next loop. The shortest outro (210ms)
        // leaves ample time.
        next_1 = nextBlobs.then(decodedSrcs);
        next_1.catch(() => {});
        await wait(exit.outroMs - FRAME_MS);
        // A late load holds the outro's last frame, the neutral pose.
        const [intro, loop] = await withLoadTimeout(next_1);
        nextTaken = true;
        if (stale()) {
          revokeAll([intro, loop]);
          return false;
        }
        show({
          variant: from,
          theme,
          src: intro,
          phase: "intro"
        });
        await nextPaint();
        await wait(ANVIL_TRANSITION_MANIFEST[to].introMs - FRAME_MS);
        if (stale()) {
          URL.revokeObjectURL(loop);
          return false;
        }
        show({
          variant: to,
          theme,
          src: loop,
          phase: "loop"
        });
        await nextPaint();
      } catch {
        if (stale()) return false;
        show({
          variant: to,
          theme,
          src: animationPath(to, theme),
          phase: "loop"
        });
      } finally {
        if (!nextTaken) void next_1?.then(revokeAll, () => {});
      }
      loopStartRef.current = performance.now();
      return true;
    };
    runningRef.current = true;
    void (async () => {
      try {
        for (;;) {
          const from = shownRef.current;
          const to = latestRef.current.target;
          if (!from || to === null || from.variant === to) return;
          if (!(await transition(from.variant, to))) return;
        }
      } finally {
        if (generationRef.current === generation) runningRef.current = false;
      }
    })();
  }, [target, theme, show]);
  const loopVariant = current?.phase === "loop" ? current.variant : null;
  useEffect(() => {
    if (!loopVariant) return;
    for (const name_0 of outroNames(loopVariant)) {
      void fetchBlob(animationPath(name_0, theme));
    }
  }, [loopVariant, theme]);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (shownRef.current) revokeLater(shownRef.current.src);
    };
  }, []);
  return {
    src: current?.src ?? null,
    holding: current !== null && current.phase === "loop" && current.variant !== target
  };
}